import { drProfileClient } from '@/common/apis/client';
import { CENTERS } from '@/common/types/centers';

export type FactorOnlineAvailability =
  | { status: 'available'; serviceId: string; userCenterId: string }
  | { status: 'unavailable'; reason: 'doctor' | 'service' };

const validId = (value: unknown) => (typeof value === 'string' && value.trim() !== '' && value !== '-1') || (typeof value === 'number' && Number.isFinite(value) && value > 0);

/** Strict, page-local validation: transport/schema failures must never mean inactive. */
export async function checkFactorOnlineAvailability(slug: string, requestedServiceId: string): Promise<FactorOnlineAvailability> {
  const path = `/api/doctors/${encodeURIComponent(slug)}/centers`;
  const { data: centers } = await drProfileClient.get(path, { timeout: 5000, params: { check_bookable_services: 'true' } });
  if (!Array.isArray(centers) || centers.some(center => !center || !validId(center.id) ||
    (center.has_bookable_services !== undefined && typeof center.has_bookable_services !== 'boolean') ||
    (center.active_booking !== undefined && typeof center.active_booking !== 'boolean'))) {
    throw new Error('Invalid doctor centers response');
  }
  const center = centers.find(item => String(item.id) === CENTERS.CONSULT);
  if (!center || center.active_booking === false || center.has_bookable_services === false) {
    return { status: 'unavailable', reason: 'doctor' };
  }
  const { data: services } = await drProfileClient.get(`${path}/${CENTERS.CONSULT}/services`, { timeout: 5000 });
  if (!Array.isArray(services) || services.some(item => !item || !item.service || !validId(item.service.id) || typeof item.can_booking !== 'boolean')) {
    throw new Error('Invalid doctor services response');
  }
  if (!services.some(item => item.can_booking)) return { status: 'unavailable', reason: 'doctor' };
  // Profile service ids can be stale: when the requested id is unknown to drprofile, fall back to a bookable one.
  const selected = services.find(item => String(item.service.id) === requestedServiceId) ?? services.find(item => item.can_booking);
  if (!selected?.can_booking) return { status: 'unavailable', reason: 'service' };
  if (!validId(center.user_center_id)) throw new Error('Missing booking center identifier');
  return { status: 'available', serviceId: String(selected.service.id), userCenterId: String(center.user_center_id) };
}
